import { getDefaultRequestState } from '@Features/utils/getDefaultRequestState';
import { CostCenter } from '@Types/api/common/dictionary';
import {
  MaterialRequisitionDocumentResponse,
  MaterialRequisitionItemFormResponse,
} from '@Types/api/form';
import { StoreOverheadResponse } from '@Types/api/form/materialRequisition/MaterialStoreOverheadResponse';
import { RequestFormHeader } from '@Types/index';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  PriceComparisonDto,
  TenderSupplier,
} from '@Features/MaterialRequest/requests/components/ComparisonSheet/ComparisonSheet.types';

interface MaterialManagest {
  step: number;
  showConfirmButton: boolean;
}

interface RequestHeaderDetails {
  mrFor: string;
  companyName: string;
  costCenterType: string;
  costCenter: string;
  costCenterId: number | null;
  compId: number | null;
  unit: string;
}

interface ModalManager {
  open: boolean;
  title: string;
  okText: string;
  isEditMode: boolean;
  modalType?: 'Material' | 'Document';
}

interface ModalItemManager {
  deleteItem: Array<number | MaterialRequisitionDocumentResponse>;
  modalType: 'Material' | 'Document';
  titleModal: string;
  messageModal: string;
  showModal: boolean;
}

interface SelectAllManager {
  isSelected: boolean;
  selectedItems: Array<string | number | MaterialRequisitionDocumentResponse>;
}

interface SearchedManager {
  isSearch: boolean;
  searchedItems: Array<
    MaterialRequisitionItemFormResponse | MaterialRequisitionDocumentResponse
  >;
}

interface MaterialRequestState {
  materialRequest: RequestFormHeader;
  materialManagest: MaterialManagest;
  materialHeaderInfo: RequestHeaderDetails;
  modalManager: ModalManager;
  modalButtonDisable: boolean;
  modalManagerItem: ModalItemManager;
  materialCostCenters: Array<CostCenter>;
  materialStoreOverhead: StoreOverheadResponse;
  mrSelectAllManager: SelectAllManager;
  mrSearchManager: SearchedManager;
  materialItemInputs: {
    activity: string;
    resource: string;
    material: string;
    ledger: string;
    plant: string;
  };
  comparisonSheetOffers: PriceComparisonDto[] | null;
  comparisonSheetSuppliers: TenderSupplier[] | null;
}

const initialState: MaterialRequestState = {
  materialRequest: getDefaultRequestState(),
  materialHeaderInfo: {
    companyName: '',
    costCenter: '',
    costCenterId: null,
    compId: null,
    costCenterType: '',
    mrFor: '',
    unit: '',
  },
  materialManagest: {
    step: 0,
    showConfirmButton: false,
  },
  modalManagerItem: {
    deleteItem: [],
    modalType: 'Material',
    titleModal: '',
    messageModal: '',
    showModal: false,
  },
  modalManager: {} as ModalManager,
  modalButtonDisable: true,
  materialCostCenters: [],
  materialStoreOverhead: {} as StoreOverheadResponse,
  mrSearchManager: {
    isSearch: false,
    searchedItems: [],
  },
  mrSelectAllManager: {
    isSelected: false,
    selectedItems: [],
  },
  materialItemInputs: {
    activity: '',
    resource: '',
    material: '',
    ledger: '',
    plant: '',
  },
  comparisonSheetOffers: null,
  comparisonSheetSuppliers: null,
};

const materialRequestSlice = createSlice({
  name: 'materialRequest',
  initialState,
  reducers: {
    setMaterialRequest: (
      state: MaterialRequestState,
      action: PayloadAction<RequestFormHeader>
    ) => {
      state.materialRequest = action.payload;
    },
    setShowConfirmButton: (
      state: MaterialRequestState,
      action: PayloadAction<boolean>
    ) => {
      state.materialManagest.showConfirmButton = action.payload;
    },
    setSwitchStep: (
      state: MaterialRequestState,
      action: PayloadAction<number>
    ) => {
      state.materialManagest.step = action.payload;
    },
    setNextStep: (
      state: MaterialRequestState,
      action: PayloadAction<number>
    ) => {
      state.materialManagest.step += action.payload;
    },

    setMaterialHeaderInfo: (
      state: MaterialRequestState,
      action: PayloadAction<RequestHeaderDetails>
    ) => {
      state.materialHeaderInfo = action.payload;
    },
    setModalManager: (
      state: MaterialRequestState,
      action: PayloadAction<ModalManager>
    ) => {
      state.modalManager = action.payload;
    },
    setModalButtonDisable: (
      state: MaterialRequestState,
      action: PayloadAction<boolean>
    ) => {
      state.modalButtonDisable = action.payload;
    },
    setModalManagerItem: (
      state: MaterialRequestState,
      action: PayloadAction<ModalItemManager>
    ) => {
      state.modalManagerItem = action.payload;
    },
    setMaterialStoreOverhead: (
      state: MaterialRequestState,
      action: PayloadAction<StoreOverheadResponse>
    ) => {
      state.materialStoreOverhead = action.payload;
    },
    setMaterialCostCenters: (
      state: MaterialRequestState,
      action: PayloadAction<Array<CostCenter>>
    ) => {
      if (state.materialCostCenters.length && action.payload.length) {
        const isExist = state.materialCostCenters.find(
          (costCenter) => costCenter.id === action.payload[0].id
        );

        if (!isExist) {
          state.materialCostCenters = [
            ...state.materialCostCenters,
            ...action.payload,
          ];
        }
        return;
      }
      state.materialCostCenters = action.payload;
    },
    setMRShowSearch: (
      state: MaterialRequestState,
      action: PayloadAction<boolean>
    ) => {
      state.mrSearchManager.isSearch = action.payload;
    },
    setMRSearchedItems: (
      state: MaterialRequestState,
      action: PayloadAction<
        Array<
          | MaterialRequisitionItemFormResponse
          | MaterialRequisitionDocumentResponse
        >
      >
    ) => {
      state.mrSearchManager.searchedItems = action.payload;
    },
    setMRSearchManagerReset: (state: MaterialRequestState) => {
      state.mrSearchManager = initialState.mrSearchManager;
    },
    setMRSelectAllManagerReset: (state: MaterialRequestState) => {
      state.mrSelectAllManager = initialState.mrSelectAllManager;
    },
    setMRSelectAllItems: (
      state: MaterialRequestState,
      action: PayloadAction<boolean>
    ) => {
      state.mrSelectAllManager.isSelected = action.payload;
    },
    setMRSelectAddItems: (
      state: MaterialRequestState,
      action: PayloadAction<
        Array<string | number | MaterialRequisitionDocumentResponse>
      >
    ) => {
      state.mrSelectAllManager.selectedItems = action.payload;
    },
    setMRUnselectItem: (
      state: MaterialRequestState,
      action: PayloadAction<number | MaterialRequisitionDocumentResponse>
    ) => {
      let newItems = [];

      if (typeof action.payload === 'object') {
        const isIncluded = !!state.mrSelectAllManager.selectedItems.find(
          (item) =>
            (item as MaterialRequisitionDocumentResponse).docName ===
            (action.payload as MaterialRequisitionDocumentResponse).docName
        );
        newItems = isIncluded
          ? state.mrSelectAllManager.selectedItems.filter(
              (item) =>
                (item as MaterialRequisitionDocumentResponse).docName !==
                (action.payload as MaterialRequisitionDocumentResponse).docName
            )
          : [...state.mrSelectAllManager.selectedItems, action.payload];
      } else {
        const isIncluded = !!state.mrSelectAllManager.selectedItems.find(
          (item) => item === action.payload
        );
        newItems = isIncluded
          ? state.mrSelectAllManager.selectedItems.filter(
              (item) => item !== action.payload
            )
          : [...state.mrSelectAllManager.selectedItems, action.payload];
      }

      if (newItems.length === 0) {
        state.mrSelectAllManager.isSelected = false;
      }

      state.mrSelectAllManager.selectedItems = newItems;
    },
    setActivityInputValue: (
      state: MaterialRequestState,
      action: PayloadAction<string>
    ) => {
      state.materialItemInputs.activity = action.payload;
    },
    setResourceInputValue: (
      state: MaterialRequestState,
      action: PayloadAction<string>
    ) => {
      state.materialItemInputs.resource = action.payload;
    },
    setPlantInputValue: (
      state: MaterialRequestState,
      action: PayloadAction<string>
    ) => {
      state.materialItemInputs.plant = action.payload;
    },
    setMaterialInputValue: (
      state: MaterialRequestState,
      action: PayloadAction<string>
    ) => {
      state.materialItemInputs.material = action.payload;
    },
    setLedgerInputValue: (
      state: MaterialRequestState,
      action: PayloadAction<string>
    ) => {
      state.materialItemInputs.ledger = action.payload;
    },
    cleanUpInputs: (state: MaterialRequestState) => {
      state.materialItemInputs = initialState.materialItemInputs;
    },
    clearForm: () => initialState,
    setComparisonSheet: (
      state,
      action: PayloadAction<PriceComparisonDto[]>
    ) => {
      state.comparisonSheetOffers = action.payload;
    },
    setMaterialSuppliers: (state, action: PayloadAction<TenderSupplier[]>) => {
      state.comparisonSheetSuppliers = action.payload;
    },
  },
});

const { actions } = materialRequestSlice;

export const materialRequestActions = {
  ...actions,
};

export default materialRequestSlice;
